import { useEffect, useState, type FormEvent } from "react";
import { collection, onSnapshot, orderBy, query, where } from "firebase/firestore";
import { paths, type SupportTicket } from "@clevis/shared-types";
import { Button, Card, CardTitle, Input, Label, Select } from "@clevis/shared-ui";
import { db } from "../../lib/firebase";
import { call } from "../../lib/callFunction";
import { useAuth } from "../../hooks/useAuth";
import { TicketThread } from "../../components/TicketThread";
import { RemoteSupportCard } from "../../components/RemoteSupportCard";

type Row = SupportTicket & { id: string };

export function SupportPage() {
  const { tenantId } = useAuth();
  const [tickets, setTickets] = useState<Row[]>([]);
  const [openId, setOpenId] = useState<string | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    subject: "",
    category: "question" as SupportTicket["category"],
    priority: "normal" as SupportTicket["priority"],
    body: "",
  });

  useEffect(() => {
    if (!tenantId) return;
    const q = query(
      collection(db, paths.supportTickets()),
      where("tenantId", "==", tenantId),
      orderBy("updatedAt", "desc"),
    );
    return onSnapshot(q, (snap) => {
      setTickets(snap.docs.map((d) => ({ id: d.id, ...(d.data() as SupportTicket) })));
    });
  }, [tenantId]);

  async function create(e: FormEvent) {
    e.preventDefault();
    if (!tenantId) return;
    setBusy(true);
    setError("");
    try {
      const res = await call<{ ticketId: string }>("createSupportTicket", { tenantId, ...form });
      setShowNew(false);
      setForm({ subject: "", category: "question", priority: "normal", body: "" });
      setOpenId(res.ticketId);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const open = openId ? tickets.find((t) => t.id === openId) : null;
  if (open) {
    return (
      <div className="mx-auto max-w-2xl">
        <TicketThread ticketId={open.id} ticket={open} onBack={() => setOpenId(null)} />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-bold">Support</h1>
        <Button onClick={() => setShowNew((v) => !v)}>{showNew ? "Cancel" : "New ticket"}</Button>
      </div>

      <RemoteSupportCard />

      {showNew ? (
        <Card>
          <CardTitle>What's going on?</CardTitle>
          <form onSubmit={create} className="space-y-3">
            <div>
              <Label>Subject</Label>
              <Input required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Category</Label>
                <Select
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value as SupportTicket["category"] })}
                >
                  <option value="question">Question</option>
                  <option value="billing">Billing</option>
                  <option value="integrations">Integrations</option>
                  <option value="bug">Something's broken</option>
                  <option value="feature">Feature request</option>
                  <option value="other">Other</option>
                </Select>
              </div>
              <div>
                <Label>Priority</Label>
                <Select
                  value={form.priority}
                  onChange={(e) => setForm({ ...form, priority: e.target.value as SupportTicket["priority"] })}
                >
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </Select>
              </div>
            </div>
            <div>
              <Label>Describe it</Label>
              <textarea
                className="min-h-[80px] w-full rounded-lg border border-steel/40 bg-white px-3 py-2 text-sm text-ink focus:border-brandorange focus:outline-none"
                required
                value={form.body}
                onChange={(e) => setForm({ ...form, body: e.target.value })}
              />
            </div>
            {error ? <p className="text-xs text-danger">{error}</p> : null}
            <Button type="submit" busy={busy}>
              Send to support
            </Button>
          </form>
        </Card>
      ) : null}

      <Card className="p-0">
        {tickets.map((t) => (
          <button
            key={t.id}
            className="flex w-full items-center justify-between gap-3 border-b border-steel/10 px-4 py-2.5 text-left last:border-0 hover:bg-cream/60"
            onClick={() => setOpenId(t.id)}
          >
            <div className="min-w-0">
              <div className="truncate text-sm font-medium text-ink">
                {t.unreadForCustomer ? <span className="mr-1 inline-block h-2 w-2 rounded-full bg-brandorange" /> : null}
                {t.subject}
              </div>
              <div className="text-xs text-steel">
                {t.category} ·{" "}
                {new Date(t.lastMessageAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
              </div>
            </div>
            <span className="shrink-0 rounded-full bg-steel/15 px-2 py-0.5 text-xs">{t.status.replaceAll("_", " ")}</span>
          </button>
        ))}
        {tickets.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-steel">No tickets. We answer fast — try us.</div>
        ) : null}
      </Card>
    </div>
  );
}
